import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import StatusBadge from './StatusBadge';
import {
  COVERAGE,
  COVERAGE_TOTALS,
  COVERAGE_PACKAGE_IDS,
  regionsByOpenSlots,
  soldPercent,
} from './sponsorCoverage';
import type { RegionCoverage as RegionCoverageData } from './sponsorCoverage';
import { packageById } from '@/pages/demo/revenue/components/revenueData';

type View = 'grid' | 'matrix';
type StateFilter = 'all' | 'California' | 'Arizona';

function Kpi({ label, value, note, icon, accent = false }: { label: string; value: string; note: string; icon: string; accent?: boolean }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-500">{label}</p>
        <span className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${accent ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-600'}`}><i className={icon}></i></span>
      </div>
      <p className="mt-2.5 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-none">{value}</p>
      <p className="mt-1.5 text-[11px] text-foreground-500">{note}</p>
    </div>
  );
}

/** A single package tier row inside a market card. */
function PackageRow({ pkg }: { pkg: RegionCoverageData['packages'][number] }) {
  return (
    <li className="flex items-center gap-3">
      <span className="min-w-0 flex-1 text-sm text-foreground-800 truncate">{pkg.name}</span>
      <span className="font-label font-semibold text-xs text-foreground-600 whitespace-nowrap tabular-nums">{pkg.filled}/{pkg.capacity}</span>
      {pkg.soldOut ? (
        <span className="inline-flex items-center gap-1 font-label font-bold uppercase tracking-wide text-[9px] px-2 py-1 rounded-sm bg-background-200 text-foreground-600 border border-background-300 whitespace-nowrap">
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-lock-line"></i></span>Sold out
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 font-label font-bold uppercase tracking-wide text-[9px] px-2 py-1 rounded-sm bg-primary-100 text-primary-700 border border-primary-200 whitespace-nowrap">
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line"></i></span>{pkg.open} open
        </span>
      )}
    </li>
  );
}

function MarketCard({ data }: { data: RegionCoverageData }) {
  const pct = soldPercent(data);
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-bold text-base text-foreground-950 leading-tight">{data.region.city}</p>
          <p className="mt-0.5 font-label uppercase tracking-widest text-[10px] text-foreground-500">
            {data.region.state === 'California' ? 'California' : 'Arizona'} · {data.region.short}
          </p>
        </div>
        {data.open > 0 ? (
          <StatusBadge tone="done">{data.open} slot{data.open === 1 ? '' : 's'} open</StatusBadge>
        ) : (
          <StatusBadge tone="alert">Waitlist</StatusBadge>
        )}
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>Slot coverage</span>
          <span className="font-label font-semibold text-foreground-700 tabular-nums">{data.filled}/{data.capacity} · {pct}%</span>
        </div>
        <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
          <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
        </div>
      </div>

      <ul className="mt-4 space-y-2.5 divide-y divide-background-100">
        {data.packages.map((pkg) => (
          <div key={pkg.packageId} className="pt-2.5 first:pt-0">
            <PackageRow pkg={pkg} />
          </div>
        ))}
      </ul>

      {data.soldOutPackages > 0 && (
        <p className="mt-4 text-[11px] text-foreground-500">
          {data.soldOutPackages} package tier{data.soldOutPackages === 1 ? '' : 's'} at capacity — offer a waitlist or an upsell.
        </p>
      )}
    </div>
  );
}

export default function RegionCoverage() {
  const [view, setView] = useState<View>('grid');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [packageFilter, setPackageFilter] = useState<string>('all');

  const filtered = useMemo(
    () => (stateFilter === 'all' ? COVERAGE : COVERAGE.filter((r) => r.region.state === stateFilter)),
    [stateFilter],
  );

  const ranked = useMemo(() => regionsByOpenSlots(filtered), [filtered]);

  const activePackages = useMemo(
    () => (packageFilter === 'all' ? COVERAGE_PACKAGE_IDS : [packageFilter]),
    [packageFilter],
  );

  const filteredTotals = useMemo(() => {
    const capacity = filtered.reduce((s, r) => s + r.capacity, 0);
    const filled = filtered.reduce((s, r) => s + r.filled, 0);
    const open = filtered.reduce((s, r) => s + r.open, 0);
    return { capacity, filled, open };
  }, [filtered]);

  const soldPct = filteredTotals.capacity ? Math.round((filteredTotals.filled / filteredTotals.capacity) * 100) : 0;
  const openMarkets = filtered.filter((r) => r.open > 0);
  const hotMarkets = ranked.filter((r) => r.open > 0).slice(0, 3);

  const stateOptions: { id: StateFilter; label: string }[] = [
    { id: 'all', label: 'All regions' },
    { id: 'California', label: 'California' },
    { id: 'Arizona', label: 'Arizona' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-lg md:text-xl text-foreground-950">Region coverage</h2>
        <DemoBadge label="Sample inventory — not live" />
      </div>

      <div className="flex items-start gap-3 bg-secondary-50 border border-secondary-200 border-l-4 border-l-secondary-500 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-secondary-900 shrink-0 mt-0.5"><i className="ri-map-pin-line"></i></span>
        <p className="text-sm text-foreground-800">
          <strong className="font-semibold">Sponsorship slot inventory.</strong> Each market sells a limited number of slots per package tier. Green means the market still has openings; grey means the tier is at capacity. Occupied slots count active, scheduled and in-review campaigns.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Open slots" value={String(filteredTotals.open)} note="Available to sell now" icon="ri-checkbox-blank-circle-line" />
        <Kpi label="Slots filled" value={`${filteredTotals.filled}/${filteredTotals.capacity}`} note={`${soldPct}% of inventory sold`} icon="ri-pie-chart-line" accent />
        <Kpi label="Markets with openings" value={`${openMarkets.length}/${filtered.length}`} note="Still have at least one slot" icon="ri-map-2-line" />
        <Kpi label="At-capacity tiers" value={String(filtered.reduce((s, r) => s + r.soldOutPackages, 0))} note="Across all markets" icon="ri-lock-line" accent />
      </div>

      {/* Best opportunities */}
      {hotMarkets.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading font-bold text-base text-foreground-950">Best openings right now</p>
            <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Markets with the most room</span>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {hotMarkets.map((r) => (
              <div key={r.region.id} className="flex items-center gap-3 rounded-md bg-primary-50 border border-primary-200 px-3.5 py-3">
                <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-sm shrink-0">{r.open}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-950 truncate">{r.region.city}</p>
                  <p className="text-[11px] text-foreground-500">{r.region.short} · open slots</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 w-16">Region</span>
          {stateOptions.map((o) => {
            const on = stateFilter === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => setStateFilter(o.id)}
                className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 w-16">Package</span>
          <button
            type="button"
            onClick={() => setPackageFilter('all')}
            className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${packageFilter === 'all' ? 'bg-secondary-500 border-secondary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-secondary-400'}`}
          >
            All packages
          </button>
          {COVERAGE_PACKAGE_IDS.map((id) => {
            const on = packageFilter === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPackageFilter(id)}
                className={`px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-secondary-500 border-secondary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-secondary-400'}`}
              >
                {packageById(id)?.name ?? id}
              </button>
            );
          })}
          <div className="ml-auto inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
            <button type="button" onClick={() => setView('grid')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${view === 'grid' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-layout-grid-line"></i></span>Markets
            </button>
            <button type="button" onClick={() => setView('matrix')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${view === 'matrix' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-table-line"></i></span>Matrix
            </button>
          </div>
        </div>
      </div>

      {/* Grid view */}
      {view === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {ranked.map((r) => {
            const shown: RegionCoverageData = packageFilter === 'all'
              ? r
              : {
                  ...r,
                  packages: r.packages.filter((p) => p.packageId === packageFilter),
                };
            return <MarketCard key={r.region.id} data={shown} />;
          })}
        </div>
      )}

      {/* Matrix view */}
      {view === 'matrix' && (
        <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                  <th className="px-4 py-3 font-semibold">Market</th>
                  {activePackages.map((id) => (
                    <th key={id} className="px-3 py-3 font-semibold text-center">{packageById(id)?.name ?? id}</th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-right">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {ranked.map((r) => {
                  const rowOpen = activePackages.reduce((s, id) => {
                    const pkg = r.packages.find((p) => p.packageId === id);
                    return s + (pkg ? pkg.open : 0);
                  }, 0);
                  return (
                    <tr key={r.region.id} className="hover:bg-background-100">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-foreground-950 font-medium">{r.region.city}</span>
                        <span className="text-foreground-400"> · {r.region.short}</span>
                      </td>
                      {activePackages.map((id) => {
                        const pkg = r.packages.find((p) => p.packageId === id);
                        if (!pkg) return <td key={id} className="px-3 py-3 text-center text-foreground-400">—</td>;
                        return (
                          <td key={id} className="px-3 py-3 text-center whitespace-nowrap">
                            {pkg.soldOut ? (
                              <span className="inline-flex items-center gap-1 font-label font-semibold text-[11px] text-foreground-500">
                                <span className="w-2 h-2 rounded-full bg-foreground-300"></span>{pkg.filled}/{pkg.capacity} full
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 font-label font-semibold text-[11px] text-primary-700">
                                <span className="w-2 h-2 rounded-full bg-primary-500"></span>{pkg.open} open
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {rowOpen > 0
                          ? <span className="font-heading font-extrabold text-foreground-950 tabular-nums">{rowOpen}</span>
                          : <StatusBadge tone="alert">Full</StatusBadge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 md:px-5 py-3 text-[11px] text-foreground-500 border-t border-background-200">
            Each cell shows how many slots remain open in that market for that package tier. Multi-region packages still occupy one slot in every market they target.
          </p>
        </div>
      )}

      <p className="text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></span>
        Slot inventory is a sample capacity model; occupancy is read from the demo sponsor ledger. Nothing is sold or reserved in this preview.
      </p>
    </div>
  );
}